import { GlobalToken, Skeleton, Table } from 'antd';
import { observer } from 'mobx-react';
import numeral from 'numeral';
import React, { useCallback } from 'react';
import styled from 'styled-components';
import { Trans, useTranslation } from 'react-i18next';

import IconArrowGreenDown from '@/assets/icon-arrow-green-down.svg?react';
import IconArrowRedUp from '@/assets/icon-arrow-red-up.svg?react';
import IconChevronDown from '@/assets/icon-chevron-down.svg?react';
import Super from '@/comp/Super';
import { ControlModeEnum, DeviceConnectionTypeEnum } from '@/constants/enums';
import { useMeasurement } from '@/hooks/useMeasurement';
import { useStores } from '@/hooks/use-stores';

const colNames = {
  NAME: 'name',
  PRESSURE_AVG: 'pressureBar_average',
  PRESSURE_MIN: 'pressureBar_minimum',
  PRESSURE_MAX: 'pressureBar_maximum',
  POWER_TOTAL: 'powerKw_sum',
  POWER_AVG: 'powerKw_average',
  POWER_MIN: 'powerKw_minimum',
  POWER_MAX: 'powerKw_maximum',
  FLOW_TOTAL: 'flowM3Min_sum',
  FLOW_AVG: 'flowM3Min_average',
  FLOW_MIN: 'flowM3Min_minimum',
  FLOW_MAX: 'flowM3Min_maximum',
  ESTIMATED_FLOW_TOTAL: 'estimatedFlowM3Min_sum',
  ESTIMATED_FLOW_AVG: 'estimatedFlowM3Min_average',
  ESTIMATED_FLOW_MIN: 'estimatedFlowM3Min_minimum',
  ESTIMATED_FLOW_MAX: 'estimatedFlowM3Min_maximum',
  ESTIMATED_POWER_TOTAL: 'estimatedKw_sum',
  ESTIMATED_POWER_AVG: 'estimatedKw_average',
  ESTIMATED_POWER_MIN: 'estimatedKw_minimum',
  ESTIMATED_POWER_MAX: 'estimatedKw_maximum',
  OIL_TEMP_AVG: 'oilTemperatureCelsius_average',
  OIL_TEMP_MIN: 'oilTemperatureCelsius_minimum',
  OIL_TEMP_MAX: 'oilTemperatureCelsius_maximum',
  DEW_TEMP_MIN: 'dewpointTemperatureCelsius_minimum',
  DEW_TEMP_MAX: 'dewpointTemperatureCelsius_maximum',
  SPECIFIC_POWER_TOTAL: 'specificPower_average',
  ESTIMATED_SPECIFIC_POWER_TOTAL: 'estimatedSpecificPower_average',
  OFF: 'off',
  LOAD: 'load',
  UNLOAD: 'unload',
};
const rowNames = {
  DEVICES_BREAKDOWN: 'devicesBreakdown',
  STATION_TOTAL: 'stationTotal',
  STATION_TOTAL_PERIOD1: 'stationTotalPeriod1',
  STATION_TOTAL_PERIOD2: 'stationTotalPeriod2',
};

const TabularData = observer(({ activityMetricsPeriod1, activityMetricsPeriod2 }) => {
  const { t } = useTranslation();
  const { tunit } = useMeasurement();
  const { investigationStore, devicesStore } = useStores();

  const isDevicesBreakdown = name => {
    return name.startsWith(rowNames.DEVICES_BREAKDOWN);
  };

  const isDevicesBreakdownPeriod = name => {
    return name.endsWith('period1') || name.endsWith('period2');
  };

  const renderFlowFunc = (...args) => {
    const [value, row, index, column] = args;

    if (row.key.includes(rowNames.STATION_TOTAL)) {
      if (column === colNames.FLOW_MIN) {
        return renderFunc(
          value,
          row,
          index,
          column,
          row[colNames.FLOW_MAX] ? colNames.FLOW_MAX : colNames.ESTIMATED_FLOW_MAX,
        );
      }
      return renderFunc(value, row, index, column);
    } else {
      switch (column) {
        case colNames.FLOW_TOTAL:
          return renderFunc(row[colNames.FLOW_TOTAL] ?? row[colNames.ESTIMATED_FLOW_TOTAL], row, index, column);
        case colNames.FLOW_AVG:
          return renderFunc(row[colNames.FLOW_AVG] ?? row[colNames.ESTIMATED_FLOW_AVG], row, index, column);
        case colNames.FLOW_MIN:
          return renderFunc(
            row[colNames.FLOW_MIN] ?? row[colNames.ESTIMATED_FLOW_MIN],
            row,
            index,
            column,
            row[colNames.FLOW_MAX] ? colNames.FLOW_MAX : colNames.ESTIMATED_FLOW_MAX,
          );
      }
    }
  };

  const renderPowerFunc = (...args) => {
    const [value, row, index, column] = args;

    if (row.key.includes(rowNames.STATION_TOTAL)) {
      if (column === colNames.POWER_MIN) {
        return renderFunc(
          value,
          row,
          index,
          column,
          row[colNames.POWER_MAX] ? colNames.POWER_MAX : colNames.ESTIMATED_POWER_MAX,
        );
      }
      return renderFunc(value, row, index, column);
    } else {
      switch (column) {
        case colNames.POWER_TOTAL:
          return renderFunc(row[colNames.POWER_TOTAL] ?? row[colNames.ESTIMATED_POWER_TOTAL], row, index, column);
        case colNames.POWER_AVG:
          return renderFunc(row[colNames.POWER_AVG] ?? row[colNames.ESTIMATED_POWER_AVG], row, index, column);
        case colNames.POWER_MIN:
          return renderFunc(
            row[colNames.POWER_MIN] ?? row[colNames.ESTIMATED_POWER_MIN],
            row,
            index,
            column,
            row[colNames.POWER_MAX] ? colNames.POWER_MAX : colNames.ESTIMATED_POWER_MAX,
          );
      }
    }
  };

  const renderSpecificPowerFunc = (...args) => {
    const [value, row, index, column] = args;
    if (row.key.includes(rowNames.STATION_TOTAL)) return renderFunc(value, row, index, column);
    else {
      switch (column) {
        case colNames.SPECIFIC_POWER_TOTAL:
          return renderFunc(
            row[colNames.SPECIFIC_POWER_TOTAL] ?? row[colNames.ESTIMATED_SPECIFIC_POWER_TOTAL],
            row,
            index,
            column,
          );
      }
    }
  };

  const renderFunc = useCallback(
    (...args) => {
      if (investigationStore.isLoadingDeviceMetricsAggregationData || investigationStore.isLoading) {
        return <Skeleton active title={{ width: '100%' }} paragraph={false} />;
      }

      const [value = 'N/A', row, , key, secondKey = ''] = args;

      const oddColumn = [
        colNames.PRESSURE_AVG,
        colNames.PRESSURE_MAX,
        colNames.PRESSURE_MIN,
        colNames.FLOW_MAX,
        colNames.FLOW_MIN,
        colNames.FLOW_AVG,
        colNames.FLOW_TOTAL,
        colNames.DEW_TEMP_MAX,
        colNames.DEW_TEMP_MIN,
        colNames.LOAD,
        colNames.UNLOAD,
        colNames.OFF,
      ];

      const obj: { children: string | JSX.Element; props: { className: string } } = {
        children: value,
        props: { className: oddColumn.includes(key) ? 'odd' : 'even' },
      };

      if (row.key !== rowNames.DEVICES_BREAKDOWN && secondKey) {
        obj.children = `${obj.children} / ${row?.[secondKey] || 'N/A'}`;
      }

      if (row.key === rowNames.DEVICES_BREAKDOWN) {
        obj.children = '';
        obj.props.className = obj.props.className.replace('odd', '');
      }

      if (
        key === colNames.SPECIFIC_POWER_TOTAL &&
        value &&
        String(value) !== 'N/A' &&
        (row.key === rowNames.STATION_TOTAL || (isDevicesBreakdown(row.key) && !isDevicesBreakdownPeriod(row.key)))
      ) {
        const number = numeral(value).value();
        const renderIconArrow = () => {
          if (number < 0) {
            return <IconArrowGreenDown />;
          } else if (number > 0) {
            return <IconArrowRedUp />;
          } else {
            return null;
          }
        };

        obj.children = (
          <div className={'flex align-center'}>
            <div>{value}</div>
            <div
              style={{
                marginLeft: 5,
                position: 'relative',
                bottom: 2,
              }}
            >
              {renderIconArrow()}
            </div>
          </div>
        );
      }

      return obj;
    },
    [investigationStore.isLoadingDeviceMetricsAggregationData, investigationStore.isLoading],
  );

  const columns = [
    {
      title: '',
      children: [
        {
          key: colNames.NAME,
          dataIndex: 'name',
          title: null,
          render: value => {
            return (
              <div
                style={{
                  display: 'inline-block',
                  verticalAlign: 'middle',
                  marginLeft: 6,
                }}
              >
                {value}
              </div>
            );
          },
        },
      ],
    },
    {
      title: t('Pressure'),
      children: [
        {
          key: colNames.PRESSURE_AVG,
          dataIndex: colNames.PRESSURE_AVG,
          title: (
            <>
              <div>{t('Avg')}</div>
              <div>({tunit('bar')})</div>
            </>
          ),
          render: (...args) => renderFunc(...args, colNames.PRESSURE_AVG),
        },
        {
          key: colNames.PRESSURE_MIN,
          dataIndex: colNames.PRESSURE_MIN,
          title: (
            <>
              <div>{t('Min / Max')}</div>
              <div>({tunit('bar')})</div>
            </>
          ),
          render: (...args) => renderFunc(...args, colNames.PRESSURE_MIN, colNames.PRESSURE_MAX),
        },
      ],
    },
    {
      title: t('Power'),
      children: [
        {
          key: colNames.POWER_TOTAL,
          dataIndex: colNames.POWER_TOTAL,
          title: (
            <div className={'metric-subtitle'}>
              <div>{t('total')}</div>
              <div>({tunit('kWh')})</div>
            </div>
          ),
          render: (...args) => renderPowerFunc(...args, colNames.POWER_TOTAL),
        },
        {
          key: colNames.POWER_AVG,
          dataIndex: colNames.POWER_AVG,
          title: (
            <div className={'metric-subtitle'}>
              <div>{t('Avg')}</div>
              <div>({tunit('kW')})</div>
            </div>
          ),
          render: (...args) => renderPowerFunc(...args, colNames.POWER_AVG),
        },
        {
          key: colNames.POWER_MIN,
          dataIndex: colNames.POWER_MIN,
          title: (
            <>
              <div>{t('Min / Max')}</div>
              <div>({tunit('kW')})</div>
            </>
          ),
          render: (...args) => renderPowerFunc(...args, colNames.POWER_MIN, colNames.POWER_MAX),
        },
      ],
    },
    {
      title: t('Flow'),
      children: [
        {
          key: colNames.FLOW_TOTAL,
          dataIndex: colNames.FLOW_TOTAL,
          title: (
            <>
              <div>{t('total')}</div>
              <div>
                <Trans i18nKey={tunit('m^3')}>
                  <Super />
                </Trans>
              </div>
            </>
          ),
          render: (...args) => renderFlowFunc(...args, colNames.FLOW_TOTAL),
        },
        {
          key: colNames.FLOW_AVG,
          dataIndex: colNames.FLOW_AVG,
          title: (
            <>
              <div>{t('Avg')}</div>
              <div>
                <Trans i18nKey={tunit('m3/min')}>
                  <Super />
                </Trans>
              </div>
            </>
          ),
          render: (...args) => renderFlowFunc(...args, colNames.FLOW_AVG),
        },
        {
          key: colNames.FLOW_MIN,
          dataIndex: colNames.FLOW_MIN,
          title: (
            <>
              <div>{t('Min / Max')}</div>
              <div>
                <Trans i18nKey={tunit('m3/min')}>
                  <Super />
                </Trans>
              </div>
            </>
          ),
          render: (...args) => renderFlowFunc(...args, colNames.FLOW_MIN, colNames.FLOW_MAX),
        },
      ],
    },
    {
      title: t('Oil temp'),
      children: [
        {
          key: colNames.OIL_TEMP_AVG,
          dataIndex: colNames.OIL_TEMP_AVG,
          title: (
            <>
              <div>{t('Avg')}</div>
              <div>({tunit('°C')})</div>
            </>
          ),
          render: (...args) => renderFunc(...args, colNames.OIL_TEMP_AVG),
        },
        {
          key: colNames.OIL_TEMP_MIN,
          dataIndex: colNames.OIL_TEMP_MIN,
          title: (
            <>
              <div>{t('Min / Max')}</div>
              <div>({tunit('°C')})</div>
            </>
          ),
          render: (...args) => renderFunc(...args, colNames.OIL_TEMP_MIN, colNames.OIL_TEMP_MAX),
        },
      ],
    },
    {
      title: t('Dew temp'),
      children: [
        {
          key: colNames.DEW_TEMP_MIN,
          dataIndex: colNames.DEW_TEMP_MIN,
          title: (
            <>
              <div>{t('Min / Max')}</div>
              <div>({tunit('°C')})</div>
            </>
          ),
          render: (...args) => renderFunc(...args, colNames.DEW_TEMP_MIN, colNames.DEW_TEMP_MAX),
        },
      ],
    },
    {
      title: t('Specific power'),
      children: [
        {
          key: colNames.SPECIFIC_POWER_TOTAL,
          dataIndex: colNames.SPECIFIC_POWER_TOTAL,
          title: (
            <>
              <div>{t('total')}</div>
              <div>
                <Trans i18nKey={tunit('kW/(m3/min)')}>
                  <Super />
                </Trans>
              </div>
            </>
          ),
          render: (...args) => renderSpecificPowerFunc(...args, colNames.SPECIFIC_POWER_TOTAL),
        },
      ],
    },
    {
      title: t('Activity'),
      children: [
        {
          key: colNames.LOAD,
          dataIndex: colNames.LOAD,
          title: <div>{t('Load')}</div>,
          render: (...args) => renderFunc(...args, colNames.LOAD),
        },
        {
          key: colNames.UNLOAD,
          dataIndex: colNames.UNLOAD,
          title: <div>{t('Unload')}</div>,
          render: (...args) => renderFunc(...args, colNames.UNLOAD),
        },
        {
          key: colNames.OFF,
          dataIndex: colNames.OFF,
          title: <div>{t('Off')}</div>,
          render: (...args) => renderFunc(...args, colNames.OFF),
        },
      ],
    },
  ];

  const calculatePercentage = (current: number, all: number) => ((current * 100) / all).toFixed(2);

  const deviceStatusPeriod1 = (activityMetricsPeriod1 ?? []).map(d =>
    investigationStore.splitSeriesDataByDevice(d.deviceId, d.series),
  );
  const deviceStatusPeriod2 = (activityMetricsPeriod2 ?? []).map(d =>
    investigationStore.splitSeriesDataByDevice(d.deviceId, d.series),
  );
  const dataSource = investigationStore.deviceMetricsAggregationData.rowsData?.map(row => getDataSourceRow(row));

  function getDataSourceRow(row) {
    if (row.children) {
      row.children = row.children.map(child => {
        const deviceExistsInPeriod1 = deviceStatusPeriod1?.find(
          value => value.deviceId === child?.key?.split('_')?.[1],
        );
        const deviceExistsInPeriod2 = deviceStatusPeriod2?.find(
          value => value.deviceId === child?.key?.split('_')?.[1],
        );
        const device = devicesStore.getDeviceById(child?.key?.split('_')?.[1]);
        const isIOController =
          device?.connectionType === DeviceConnectionTypeEnum.IO && device?.controlMode === ControlModeEnum.OnOff;

        if (!isDevicesBreakdownPeriod(child?.key) && deviceExistsInPeriod1 && deviceExistsInPeriod2) {
          return {
            ...child,
            off:
              deviceExistsInPeriod1.off === 0 || deviceExistsInPeriod2.off === 0
                ? isIOController
                  ? 'N/A'
                  : '0%'
                : `${calculatePercentage(
                    deviceExistsInPeriod1.off + deviceExistsInPeriod2.off,
                    deviceExistsInPeriod1.length + deviceExistsInPeriod2.length,
                  )}%`,
            load:
              deviceExistsInPeriod1.load === 0 || deviceExistsInPeriod2.load === 0
                ? isIOController
                  ? 'N/A'
                  : '0%'
                : `${calculatePercentage(
                    deviceExistsInPeriod1.load + deviceExistsInPeriod2.load,
                    deviceExistsInPeriod1.length + deviceExistsInPeriod2.length,
                  )}%`,
            unload:
              deviceExistsInPeriod1.unload === 0 || deviceExistsInPeriod2.unload === 0
                ? isIOController
                  ? 'N/A'
                  : '0%'
                : `${calculatePercentage(
                    deviceExistsInPeriod1.unload + deviceExistsInPeriod2.unload,
                    deviceExistsInPeriod1.length + deviceExistsInPeriod2.length,
                  )}%`,
          };
        }

        if (child?.key.endsWith('period1') && deviceExistsInPeriod1) {
          return {
            ...child,
            off:
              deviceExistsInPeriod1.off === 0
                ? isIOController
                  ? 'N/A'
                  : '0%'
                : `${calculatePercentage(deviceExistsInPeriod1.off, deviceExistsInPeriod1.length)}%`,
            load:
              deviceExistsInPeriod1.load === 0
                ? isIOController
                  ? 'N/A'
                  : '0%'
                : `${calculatePercentage(deviceExistsInPeriod1.load, deviceExistsInPeriod1.length)}%`,
            unload:
              deviceExistsInPeriod1.unload === 0
                ? isIOController
                  ? 'N/A'
                  : '0%'
                : `${calculatePercentage(deviceExistsInPeriod1.unload, deviceExistsInPeriod1.length)}%`,
          };
        }

        if (child?.key.endsWith('period2') && deviceExistsInPeriod2) {
          return {
            ...child,
            off:
              deviceExistsInPeriod2.off === 0
                ? isIOController
                  ? 'N/A'
                  : '0%'
                : `${calculatePercentage(deviceExistsInPeriod2.off, deviceExistsInPeriod2.length)}%`,
            load:
              deviceExistsInPeriod2.load === 0
                ? isIOController
                  ? 'N/A'
                  : '0%'
                : `${calculatePercentage(deviceExistsInPeriod2.load, deviceExistsInPeriod2.length)}%`,
            unload:
              deviceExistsInPeriod2.unload === 0
                ? isIOController
                  ? 'N/A'
                  : '0%'
                : `${calculatePercentage(deviceExistsInPeriod2.unload, deviceExistsInPeriod2.length)}%`,
          };
        }

        return { ...child };
      });

      return {
        ...row,
        name: (
          <div>
            <div className={'row-title'}>{t(row.title)}</div>
            <div className={'row-subtitle'}>{t(row.subtitle)}</div>
          </div>
        ),
        children: row.children.map(row => getDataSourceRow(row)),
      };
    } else {
      return {
        ...row,
        name: (
          <>
            <div className={'row-title'}>{row.subtitle}</div>
            <div className={'row-subtitle'}>{row.title}</div>
          </>
        ),
      };
    }
  }

  return (
    <div className="tabular-container">
      <Container>
        <Table
          columns={columns}
          dataSource={dataSource}
          scroll={{ x: '500px' }}
          size="small"
          defaultExpandedRowKeys={[rowNames.STATION_TOTAL, rowNames.DEVICES_BREAKDOWN]}
          expandable={{
            expandIconColumnIndex: 0,
            defaultExpandAllRows: true,
            expandIcon: ({ expanded, onExpand, record }) => {
              if (!record.children) return null;

              return expanded ? (
                <IconChevronDown
                  onClick={e => onExpand(record, e as unknown as React.MouseEvent<HTMLElement, MouseEvent>)}
                />
              ) : (
                <IconChevronDown
                  style={{
                    transform: 'rotate(-90deg)',
                  }}
                  onClick={e => onExpand(record, e as unknown as React.MouseEvent<HTMLElement, MouseEvent>)}
                />
              );
            },
          }}
          pagination={false}
        />
      </Container>
    </div>
  );
});

const Container = styled.div<{ theme: { token: GlobalToken } }>`
  .ant-table {
    background-color: transparent;
    color: ${({ theme: { token } }) => token.colorText};

    .ant-table-tbody > tr > td.ant-table-cell-row-hover {
      background: ${({ theme: { token } }) => token.colorBgContainer};
    }

    .ant-table-thead > tr > td:first-child {
      visibility: hidden;
    }

    tr:not(.ant-table-placeholder) {
      td {
        border-bottom: 1px solid ${({ theme: { token } }) => token.colorBorder};

        &:first-child {
          display: flex;
          border-bottom: none;
          font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
          color: ${({ theme: { token } }) => token.colorTextSecondary};

          svg {
            margin-top: ${({ theme: { token } }) => token.marginXXS}px;
          }
        }

        &.odd {
          background-color: ${({ theme: { token } }) => token.colorBgContainer};
        }

        &.even {
        }
      }

      &[data-row-key='${rowNames.DEVICES_BREAKDOWN}'] {
        td {
          &.odd {
            background-color: transparent;
          }
        }
      }
    }

    .ant-table-thead {
      tr {
        th {
          background-color: transparent;
          border-bottom: 2px solid ${({ theme: { token } }) => token.colorBorder};

          &:first-child {
            border-bottom: none;
          }

          td {
          }
        }

        &:nth-child(1) {
          th {
            color: ${({ theme: { token } }) => token.colorText};
            border-bottom: 0;
            text-align: left;
          }
        }

        &:nth-child(2) {
          th {
            color: ${({ theme: { token } }) => token.colorTextSecondary};
          }
        }

        &:last-child {
          th {
            color: ${({ theme: { token } }) => token.colorTextSecondary};
          }
        }
      }
    }

    tr.ant-table-placeholder {
      display: none;
    }

    .ant-table-cell::before {
      display: none;
    }

    .ant-table-tbody {
      > tr.ant-table-row:hover {
        > td {
          background-color: transparent;

          &.odd {
            background-color: ${({ theme: { token } }) => token.colorBgContainer};
          }
        }
      }

      > tr.ant-table-row:hover[data-row-key='${rowNames.DEVICES_BREAKDOWN}'] {
        > td {
          &.odd {
            background-color: transparent;
          }
        }
      }
    }

    .row-title {
      font-size: ${({ theme: { token } }) => token.fontSize}px;
      color: ${({ theme: { token } }) => token.colorText};
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      line-clamp: 2;
      -webkit-box-orient: vertical;
    }

    .row-subtitle {
    }
  }

  .ant-table:not(.ant-table-bordered) .ant-table-tbody > tr.ant-table-row:hover > td:not(:first-child),
  .ant-table:not(.ant-table-bordered)
    .ant-table-tbody
    > tr.ant-table-row:hover
    + tr.ant-table-row
    > td:not(:first-child) {
    border-top-color: ${({ theme: { token } }) => token.colorBorder};
    border-bottom-color: ${({ theme: { token } }) => token.colorBorder};
  }

  .ant-table:not(.ant-table-bordered) .ant-table-tbody > tr > td:not(:first-child),
  .ant-table:not(.ant-table-bordered) .ant-table-tbody > tr:last-child > td:not(:first-child),
  .ant-table:not(.ant-table-bordered) .ant-table-tbody > tr.ant-table-row:last-child:hover > td:not(:first-child) {
    border-top-color: ${({ theme: { token } }) => token.colorBorder};
    border-bottom-color: ${({ theme: { token } }) => token.colorBorder};
  }

  .ant-table:not(.ant-table-bordered) .ant-table-tbody > tr > td:first-child,
  .ant-table:not(.ant-table-bordered) .ant-table-tbody > tr > td:first-child:hover,
  .ant-table:not(.ant-table-bordered) .ant-table-tbody > tr.ant-table-row:last-child:hover > td:first-child {
    border-top-color: transparent;
    border-bottom-color: transparent;
  }
`;

export default TabularData;
